import { useEffect, useRef, useState } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Fab from "@material-ui/core/Fab";
import Grid from "@material-ui/core/Grid";
import Chip from "@material-ui/core/Chip";
import Snackbar from "@material-ui/core/Snackbar";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";
import AlertDialogSlide from "../../AlertDialogSlide";
import CommonDialog from "../../CommonDialog";
import { DIALOG_FIELD_TYPES } from "../../../common/Stores/constant/constant";
import DeliveryTabs from "../DeliveryTabs";
import {
  getManuals,
  createManual,
  uploadManualToBlob,
  updateManualUploadStatus,
  updateManualDetails,
  updateManualPublished,
  deleteManual,
  getManualHtml
} from "./service";

// uploadStatus の表示ラベル。UPLOADED (正常) は一覧に表示しないため、
// 異常時 (アップロード中 / 失敗) のバッジ表示にのみ使う
const uploadStatusLabel = uploadStatus => {
  if (uploadStatus === "UPLOADING") return "アップロード中";
  if (uploadStatus === "FAILED") return "アップロード失敗";
  return "";
};

const formatDateTime = value => {
  if (!value) return "-";
  return new Date(value).toLocaleString("ja-JP");
};

const formatSize = sizeBytes => {
  if (!sizeBytes && sizeBytes !== 0) return "-";
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${Math.round(sizeBytes / 1024)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
};

// 社員情報登録などの既存ダイアログと同じ CommonDialog を使う (入力 UI を統一)
const uploadFieldList = [
  {
    name: "title",
    label: "タイトル",
    type: DIALOG_FIELD_TYPES.Text,
    maxLength: 100
  },
  {
    name: "memo",
    label: "メモ",
    type: DIALOG_FIELD_TYPES.Text,
    maxLength: 2000,
    multiline: true,
    rows: 8,
    guidMessage: "最大2000文字"
  }
];

const editFieldList = [
  {
    name: "title",
    label: "タイトル",
    type: DIALOG_FIELD_TYPES.Text,
    isRequired: true,
    maxLength: 100
  },
  {
    name: "memo",
    label: "メモ",
    type: DIALOG_FIELD_TYPES.Text,
    maxLength: 2000,
    multiline: true,
    rows: 8,
    guidMessage: "最大2000文字"
  }
];

const Manuals = _ => {
  const [manuals, setManuals] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState("");

  // アップロード確認ダイアログ
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = useRef(null);

  // タイトル編集ダイアログ
  const [editTarget, setEditTarget] = useState(null);

  // 公開 / 非公開 / 削除の確認ダイアログ
  const [publishTarget, setPublishTarget] = useState(null);
  const [unpublishTarget, setUnpublishTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // メモ全文表示ダイアログ
  const [memoTarget, setMemoTarget] = useState(null);

  // プレビューダイアログ
  const [previewManual, setPreviewManual] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  // 現在プレビュー対象として要求中のマニュアル Id。
  // 取得完了時に一致を確認し、古いレスポンスで別マニュアルの HTML を
  // 表示してしまう競合を防ぐ
  const previewRequestIdRef = useRef(null);

  const fetchManuals = async () => {
    const res = await getManuals();
    if (res) setManuals(res);
  };

  useEffect(() => {
    fetchManuals();
  }, []);

  const handleFileSelected = e => {
    const file = e.target.files[0];
    if (!file) return;
    const fileName = file.name;
    if (!/\.(html|htm)$/i.test(fileName)) {
      setMessage("HTMLファイル（.html / .htm）を選択してください。");
      e.target.value = "";
      return;
    }
    setUploadFile(file);
    e.target.value = "";
  };

  const closeUploadDialog = () => {
    setUploadFile(null);
  };

  // 1) レコード作成 + 書き込み用 SAS URL 発行 → 2) Blob へ直接アップロード →
  // 3) 完了/失敗を通知（log-rd-admin の共有資料と同じ 3 ステップ）
  const proceedUpload = async values => {
    if (!uploadFile || isProcessing) return;
    setIsProcessing(true);
    const created = await createManual({
      Title: (values.title || "").trim() || undefined,
      Memo: (values.memo || "").trim() || undefined,
      FileName: uploadFile.name,
      SizeBytes: uploadFile.size
    });
    if (!created) {
      // レコード作成前の失敗はダイアログを開いたままにし、入力済みの
      // タイトル・メモを失わせない（そのまま再実行できる）
      setIsProcessing(false);
      setMessage("マニュアルのアップロードに失敗しました。");
      return;
    }
    const isUploaded = await uploadManualToBlob(created.UploadUrl, uploadFile);
    const statusUpdated = await updateManualUploadStatus(
      created.Id,
      isUploaded ? "UPLOADED" : "FAILED"
    );
    setIsProcessing(false);
    closeUploadDialog();
    if (!statusUpdated) {
      // 通知 API が失敗すると uploadStatus が UPLOADING のまま残る。
      // スタック行と同じ扱いで、行の削除 → 再アップロードで復旧してもらう
      setMessage(
        "アップロード状態の更新に失敗しました。該当の行を削除して再度アップロードしてください。"
      );
      await fetchManuals();
      return;
    }
    setMessage(
      isUploaded
        ? "マニュアルをアップロードしました。"
        : "マニュアルのアップロードに失敗しました。失敗した行を削除して再度アップロードしてください。"
    );
    await fetchManuals();
  };

  const openEdit = manual => {
    setEditTarget(manual);
  };

  const closeEdit = () => {
    setEditTarget(null);
  };

  const proceedEdit = async values => {
    if (!editTarget || isProcessing) return;
    const title = (values.title || "").trim();
    if (!title) {
      setMessage("タイトルを入力してください。");
      return;
    }
    setIsProcessing(true);
    const res = await updateManualDetails(editTarget.Id, {
      Title: title,
      Memo: (values.memo || "").trim()
    });
    setIsProcessing(false);
    if (!res) {
      // 失敗時はダイアログを閉じない（入力内容を保持してそのまま再試行できる）
      setMessage("変更の保存に失敗しました。もう一度お試しください。");
      return;
    }
    closeEdit();
    setMessage("変更を保存しました。");
    await fetchManuals();
  };

  const proceedPublish = async () => {
    if (!publishTarget || isProcessing) return;
    setIsProcessing(true);
    const res = await updateManualPublished(publishTarget.Id, true);
    setIsProcessing(false);
    setPublishTarget(null);
    if (!res) {
      setMessage("マニュアルの公開に失敗しました。");
      return;
    }
    setMessage("マニュアルを公開しました。");
    await fetchManuals();
  };

  const proceedUnpublish = async () => {
    if (!unpublishTarget || isProcessing) return;
    setIsProcessing(true);
    const res = await updateManualPublished(unpublishTarget.Id, false);
    setIsProcessing(false);
    setUnpublishTarget(null);
    if (!res) {
      setMessage("マニュアルの非公開化に失敗しました。");
      return;
    }
    setMessage("マニュアルを非公開にしました。");
    await fetchManuals();
  };

  const proceedDelete = async () => {
    if (!deleteTarget || isProcessing) return;
    setIsProcessing(true);
    const res = await deleteManual(deleteTarget.Id);
    setIsProcessing(false);
    setDeleteTarget(null);
    if (!res) {
      setMessage("マニュアルの削除に失敗しました。");
      return;
    }
    setMessage("マニュアルを削除しました。");
    await fetchManuals();
  };

  const openPreview = async manual => {
    previewRequestIdRef.current = manual.Id;
    setPreviewManual(manual);
    setPreviewHtml("");
    const html = await getManualHtml(manual.Id);
    // 取得中に別のマニュアルを開いた / ダイアログを閉じた場合は結果を破棄する
    // （レスポンス順の入れ替わりで別マニュアルの HTML を表示しないため）
    if (previewRequestIdRef.current !== manual.Id) return;
    if (html === null) {
      previewRequestIdRef.current = null;
      setPreviewManual(null);
      setMessage("プレビューの取得に失敗しました。");
      return;
    }
    setPreviewHtml(html);
  };

  const closePreview = () => {
    previewRequestIdRef.current = null;
    setPreviewManual(null);
    setPreviewHtml("");
  };

  return (
    <Grid className="dashboard">
      <Typography variant="h5" className="dashboard-title text-bold">
        ユーザー配信
      </Typography>
      {/* 配信コンテンツ種別のタブ。将来「お知らせ」等をここに追加する */}
      <DeliveryTabs />
      <Grid container justify="flex-end" className="mb-5">
        <input
          type="file"
          id="manual-file"
          accept=".html,.htm"
          ref={fileInputRef}
          onChange={handleFileSelected}
          style={{ display: "none" }}
        />
        <Fab
          className="white-text button-logWalk button-feature-flag mr-5"
          variant="extended"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
        >
          <AddIcon />
          マニュアルを追加
        </Fab>
      </Grid>
      <TableContainer className="dashboard-loguibld-table background-white">
        <Grid>
          <TableContainer>
            <Table size="small" aria-label="table">
              <TableHead className="table-head">
                <TableRow>
                  <TableCell className="white-text text-center">
                    公開状態
                  </TableCell>
                  <TableCell className="white-text text-center">
                    タイトル
                  </TableCell>
                  <TableCell className="white-text text-center">
                    メモ
                  </TableCell>
                  <TableCell className="white-text text-center">
                    サイズ
                  </TableCell>
                  <TableCell className="white-text text-center">
                    登録者
                  </TableCell>
                  <TableCell className="white-text text-center">
                    登録日時
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody className="table-body">
                {manuals.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center">
                      アップロード済みのマニュアルはありません
                    </TableCell>
                  </TableRow>
                )}
                {manuals.map(manual => (
                  <TableRow
                    key={manual.Id}
                    // 公開中の行は背景色でも目立たせる
                    style={
                      manual.IsPublished
                        ? { backgroundColor: "#e8f5e9" }
                        : undefined
                    }
                  >
                    {/* 公開状態: 公開中はタグ + 公開日時をセットで表示する */}
                    <TableCell className="text-center">
                      {manual.IsPublished ? (
                        <>
                          <Chip
                            size="small"
                            label="公開中"
                            style={{
                              backgroundColor: "#2e7d32",
                              color: "#fff",
                              fontWeight: "bold"
                            }}
                          />
                          <div style={{ fontSize: 12, marginTop: 4 }}>
                            {formatDateTime(manual.PublishedAt)}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: "#999" }}>非公開</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {manual.Title}
                      {/* 正常 (UPLOADED) 以外の行だけ理由を表示する
                          (公開・プレビューできない理由がわかるように) */}
                      {manual.UploadStatus !== "UPLOADED" && (
                        <span className="error">
                          （{uploadStatusLabel(manual.UploadStatus)}）
                        </span>
                      )}
                    </TableCell>
                    {/* メモは省略表示。クリックまたは Enter/Space で全文モーダルを開く */}
                    <TableCell
                      className="text-center"
                      style={{
                        maxWidth: 240,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        cursor: manual.Memo ? "pointer" : "default"
                      }}
                      title={manual.Memo ? "クリックで全文を表示" : ""}
                      role={manual.Memo ? "button" : undefined}
                      tabIndex={manual.Memo ? 0 : undefined}
                      aria-label={manual.Memo ? "メモの全文を表示" : undefined}
                      onClick={() => {
                        if (manual.Memo) setMemoTarget(manual);
                      }}
                      onKeyDown={event => {
                        if (!manual.Memo) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setMemoTarget(manual);
                        }
                      }}
                    >
                      {manual.Memo || "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      {formatSize(manual.SizeBytes)}
                    </TableCell>
                    <TableCell className="text-center">
                      {manual.CreatedByName || "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      {formatDateTime(manual.CreatedAt)}
                    </TableCell>
                    <TableCell
                      className="padding-last-td s-ta-r text-left"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {/* 公開/公開解除は編集等と同じ操作列にまとめる */}
                      {manual.IsPublished ? (
                        <Button
                          size="small"
                          variant="outlined"
                          className="mr-5"
                          disabled={isProcessing}
                          onClick={() => setUnpublishTarget(manual)}
                        >
                          公開解除
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          className="mr-5"
                          disabled={
                            isProcessing || manual.UploadStatus !== "UPLOADED"
                          }
                          onClick={() => setPublishTarget(manual)}
                        >
                          公開
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        className="mr-5"
                        disabled={isProcessing}
                        onClick={() => openEdit(manual)}
                      >
                        編集
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        className="mr-5"
                        disabled={manual.UploadStatus !== "UPLOADED"}
                        onClick={() => openPreview(manual)}
                      >
                        プレビュー
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={manual.IsPublished || isProcessing}
                        onClick={() => setDeleteTarget(manual)}
                      >
                        削除
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </TableContainer>

      {/* アップロード確認（既存ダイアログと同じ CommonDialog を使用）。
          開くたびに Formik を initialValues から作り直すため条件付きで mount する */}
      {!!uploadFile && (
        <CommonDialog
          title="マニュアル登録"
          description={`ファイル名: ${uploadFile.name}`}
          isDialogShow={!!uploadFile}
          initialValues={{
            title: uploadFile.name.replace(/\.(html|htm)$/i, ""),
            memo: ""
          }}
          onSubmit={proceedUpload}
          onClose={closeUploadDialog}
          fieldList={uploadFieldList}
          submitButtonTitle="アップロード"
          isSingleColumn
        />
      )}

      {/* マニュアル編集 (タイトル・メモ) */}
      {!!editTarget && (
        <CommonDialog
          title="マニュアル編集"
          isDialogShow={!!editTarget}
          initialValues={{
            title: editTarget.Title,
            memo: editTarget.Memo || ""
          }}
          onSubmit={proceedEdit}
          onClose={closeEdit}
          fieldList={editFieldList}
          submitButtonTitle="保存"
          isSingleColumn
        />
      )}

      <AlertDialogSlide
        cb={proceedPublish}
        contentText="公開できるマニュアルは1件のみです。現在公開中のマニュアルは非公開になります。"
        handleClose={() => setPublishTarget(null)}
        negativeBntText="キャンセル"
        open={!!publishTarget}
        positiveBntText="公開する"
        title={
          publishTarget ? `「${publishTarget.Title}」を公開しますか？` : ""
        }
      />

      <AlertDialogSlide
        cb={proceedUnpublish}
        contentText="非公開にすると、Log Base のヘルプからマニュアルが表示できなくなります。"
        handleClose={() => setUnpublishTarget(null)}
        negativeBntText="キャンセル"
        open={!!unpublishTarget}
        positiveBntText="非公開にする"
        title={
          unpublishTarget
            ? `「${unpublishTarget.Title}」を非公開にしますか？`
            : ""
        }
      />

      <AlertDialogSlide
        cb={proceedDelete}
        contentText="削除したマニュアルは一覧に表示されなくなります。"
        handleClose={() => setDeleteTarget(null)}
        negativeBntText="キャンセル"
        open={!!deleteTarget}
        positiveBntText="削除する"
        title={
          deleteTarget ? `「${deleteTarget.Title}」を削除しますか？` : ""
        }
      />

      {/* メモ全文表示 */}
      <Dialog
        open={!!memoTarget}
        onClose={() => setMemoTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {memoTarget ? `メモ: ${memoTarget.Title}` : ""}
        </DialogTitle>
        <DialogContent>
          {/* 改行を保持して全文表示する */}
          <Typography
            variant="body2"
            style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {memoTarget ? memoTarget.Memo : ""}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMemoTarget(null)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* プレビュー（サンドボックス付き iframe。srcDoc なので別オリジン扱いになる） */}
      <Dialog
        open={!!previewManual}
        onClose={closePreview}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {previewManual ? `プレビュー: ${previewManual.Title}` : ""}
        </DialogTitle>
        <DialogContent style={{ height: "70vh", padding: 0 }}>
          {previewHtml ? (
            <iframe
              srcDoc={previewHtml}
              sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
              referrerPolicy="no-referrer"
              title={previewManual ? previewManual.Title : "プレビュー"}
              style={{ width: "100%", height: "100%", border: 0 }}
            />
          ) : (
            <Typography variant="body2" className="text-center">
              読み込み中...
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePreview}>閉じる</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!message}
        autoHideDuration={4000}
        onClose={() => setMessage("")}
        message={message}
      />
    </Grid>
  );
};

export default Manuals;
